import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Skeleton,
  Button,
  useTheme,
  Fade,
} from "@mui/material";
import {
  Pen,
  KeyRound,
  ArrowUp,
  ArrowDown,
  SearchX,
  ArrowLeft,
  Link2,
} from "lucide-react";
import NoUserFound from "./NoUsers";
import UserLoader from "./UserLoader";

const teamColors = {
  Admin: "#7C3AED",
  Designer: "#EC4899",
  "Sales Executive": "#F59E0B",
  Support: "#10B981",
};

export default function UsersTable({
  users = [],
  totalCount = 0,
  loading = false,
  canUpdate = false,
  onEdit = () => {},
  onReset = () => {},
  onClearSearch = () => {},
}) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const SKELETON_ROWS = 6;

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedUsers = useMemo(() => {
    if (!sortField) return users;
    return [...users].sort((a, b) => {
      const A = (a[sortField] || "").toString().toLowerCase();
      const B = (b[sortField] || "").toString().toLowerCase();
      if (A < B) return sortDir === "asc" ? -1 : 1;
      if (A > B) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDir]);

  const roleColors = {
    "super admin": theme.roleColors.superadmin,
    admin: theme.roleColors.admin,
    manager: theme.roleColors.manager,
    user: theme.roleColors.user,
    employee: theme.roleColors.employee,
  };

  const hexToRgba = (hex, alpha = 1) => {
    const h = hex.replace("#", "");
    const full =
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const num = parseInt(full, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getRoleColor = (r) => {
    if (!r) return "#6B7280";
    const key =
      typeof r === "object" ? (r.name || "").toLowerCase() : r.toLowerCase();
    return roleColors[key] || "#6B7280";
  };

  const sortIcon = (field) =>
    sortField !== field ? (
      <ArrowUp size={12} opacity={0.25} />
    ) : sortDir === "asc" ? (
      <ArrowUp size={12} color={theme.palette.primary.main} />
    ) : (
      <ArrowDown size={12} color={theme.palette.primary.main} />
    );

  const showNoResults = !loading && users.length === 0 && totalCount > 0;
  const showEmpty = !loading && totalCount === 0;

  if (showNoResults) {
    return <NoUserFound />;
  }

  if (loading) {
    return <UserLoader />;
  }

  if (showEmpty) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>No users found</Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #F0F0F0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
          overflow: "hidden",
          "@keyframes fadeInRow": {
            from: { opacity: 0, transform: "translateY(8px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {[
                { key: "name", label: "User" },
                { key: "email", label: "Email" },
                { key: "team", label: "Teams" },
                { key: "status", label: "Status" },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "#6B7280",
                    cursor: "pointer",
                    userSelect: "none",
                    bgcolor: "#F9FAFB",
                    borderBottom: "1px solid #F0F0F0",
                    py: 1.5,
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{col.label}</span>
                    {sortIcon(col.key)}
                  </Stack>
                </TableCell>
              ))}

              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "#6B7280",
                  bgcolor: "#F9FAFB",
                  borderBottom: "1px solid #F0F0F0",
                  py: 1.5,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading &&
              sortedUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((u, idx) => {
                  const initials = (u.name || "U")
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();

                  const isActive = u.status === "active";

                  return (
                    <TableRow
                      key={u._id || idx}
                      sx={{
                        animation: `fadeInRow 0.35s cubic-bezier(0.23, 1, 0.32, 1) ${idx * 0.06}s both`,
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          bgcolor: "#FAFBFC",
                        },
                        "& td": {
                          borderBottom: "1px solid #F5F5F5",
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              fontWeight: 700,
                              fontSize: 13,
                              background: `linear-gradient(135deg, ${theme.avatarGradients_1.avatarStart} 0%, ${theme.avatarGradients_1.avatarCenter} 50%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                              color: "#fff",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: `0 4px 12px ${theme.palette.primary.main}25`,
                              },
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: "#1F2937",
                            }}
                          >
                            {u.name}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#6B7280",
                          }}
                        >
                          {u.email}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {u.team ? (
                          <Chip
                            size="small"
                            label={u.team}
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              fontSize: 11,
                              letterSpacing: "0.03em",
                              textTransform: "uppercase",
                              height: 26,
                              bgcolor: hexToRgba(
                                teamColors[u.team] || "#6B7280",
                                0.1
                              ),
                              color: teamColors[u.team] || "#6B7280",
                              border: `1px solid ${hexToRgba(
                                teamColors[u.team] || "#6B7280",
                                0.15
                              )}`,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                bgcolor: hexToRgba(
                                  teamColors[u.team] || "#6B7280",
                                  0.18
                                ),
                              },
                            }}
                          />
                        ) : (
                          <Typography color="text.secondary" fontSize={14}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? "Active" : "Inactive"}
                          sx={{
                            fontWeight: 600,
                            fontSize: 11,
                            letterSpacing: "0.03em",
                            textTransform: "uppercase",
                            height: 26,
                            borderRadius: 1.5,
                            bgcolor: isActive
                              ? "rgba(16, 185, 129, 0.1)"
                              : "rgba(239, 68, 68, 0.1)",
                            color: isActive ? "#059669" : "#DC2626",
                            border: `1px solid ${
                              isActive
                                ? "rgba(16, 185, 129, 0.18)"
                                : "rgba(239, 68, 68, 0.18)"
                            }`,
                            transition: "all 0.2s ease",
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{ display: "flex", justifyContent: "end" }}
                        >
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              onClick={() => canUpdate && onEdit(u)}
                              disabled={!canUpdate}
                              size="small"
                              sx={{
                                width: 36,
                                height: 36,
                                border: "1px solid #E5E7EB",
                                borderRadius: "50%",
                                pointerEvents: canUpdate ? "auto" : "none",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  borderColor: theme.palette.primary.main,
                                  bgcolor: `${theme.palette.primary.main}08`,
                                  transform: "translateY(-1px)",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                },
                              }}
                            >
                              <Pen size={16} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Reset Password" arrow>
                            <IconButton
                              onClick={() => canUpdate && onReset(u)}
                              disabled={!canUpdate}
                              size="small"
                              sx={{
                                width: 36,
                                height: 36,
                                border: "1px solid #E5E7EB",
                                borderRadius: "50%",
                                pointerEvents: canUpdate ? "auto" : "none",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  borderColor: theme.palette.primary.main,
                                  bgcolor: `${theme.palette.primary.main}08`,
                                  transform: "translateY(-1px)",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                },
                              }}
                            >
                              <KeyRound size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sortedUsers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, n) => setPage(n)}
          rowsPerPageOptions={[5, 8, 10, 25]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Rows:"
          sx={{
            borderTop: "1px solid #F0F0F0",
            "& .MuiTablePagination-selectLabel": {
              fontSize: 13,
              color: "#6B7280",
              fontWeight: 500,
            },
            "& .MuiTablePagination-displayedRows": {
              fontSize: 13,
              color: "#6B7280",
              fontWeight: 500,
            },
            "& .MuiTablePagination-select": {
              fontSize: 13,
              fontWeight: 600,
            },
            "& .MuiTablePagination-actions button": {
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: `${theme.palette.primary.main}08`,
              },
            },
          }}
        />
      </TableContainer>
    </Fade>
  );
}

UsersTable.propTypes = {
  users: PropTypes.array,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  canUpdate: PropTypes.bool,
  onEdit: PropTypes.func,
  onReset: PropTypes.func,
  onClearSearch: PropTypes.func,
};
